import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Form, Select, Divider, Button } from "antd";

import { selectProvidersFacility } from "../../../../../store/selector";
import { SingleSelect } from "../../../../customComponent/CustomSelect";
import { OTHER, STATE } from "../../constants";
import { sizes } from "../../../../../constant/styles";
import { getFieldsToUpdateOnProviderChange } from "../../utils/provider";
import useConditionalValidation from "./utils/useConditionalValidation";
import { getStateCode } from "../../../../../constant/states";

const { Option } = Select;

export const GenericFacilityItem = ({
  label,
  required,
  rules = [],
  placeholder,
  allowOther,
  formToFacilityFieldMap,
  form,
  shouldValidateFields = false,
  otherOptionId,

  ...props
}) => {
  useConditionalValidation(props.name, shouldValidateFields);
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const facilities = useSelector(selectProvidersFacility);
  const selectedFacility = Form.useWatch(props.name, form);
  const formSectionName = props.name[0];

  useEffect(() => {
    if (selectedFacility) {
      form.validateFields([props.name]);
    }
  }, [selectedFacility]);

  const updateFormOnFacilityChange = async (value, option) => {
    if (option.key === "other") {
      // `props.name` is an antd name PATH (an array). Spreading it as a computed object key
      // stringifies it to "section,field" and writes a junk top-level field, so the facility is
      // never set to OTHER and the free-text inputs never reveal. setFieldValue is path-aware —
      // same call `selectOther` below already makes for the dropdownRender button. MVP-31738.
      form.setFieldValue(props.name, OTHER);
      return;
    }
    const facilityId = Number(option.key);
    const fieldsToUpdate = getFieldsToUpdateOnProviderChange(facilities, facilityId, formToFacilityFieldMap);

    if (fieldsToUpdate[STATE]) {
      fieldsToUpdate[STATE] = getStateCode(fieldsToUpdate[STATE]);
    }

    form.setFieldsValue({ [formSectionName]: { ...fieldsToUpdate } });
  };

  // When `otherOptionId` is set, restore the hand-written form's "Other" affordance: a targetable
  // dropdownRender BUTTON (id = otherOptionId) the e2e specs click, instead of an in-list option. Selecting
  // it sets the facility field to OTHER (revealing the free-text facility inputs). Opt-in — facilities
  // without an id keep the ordinary in-list "Other" option. MVP-31021, Jehad QA.
  const selectOther = () => {
    form.setFieldValue(props.name, OTHER);
    setIsOpen(false);
  };

  const otherOptionProps = otherOptionId
    ? {
        open: isOpen,
        onDropdownVisibleChange: setIsOpen,
        dropdownRender: (menu) => (
          <>
            {menu}
            <Divider style={{ margin: "8px 0" }} />
            <Button
              id={otherOptionId}
              type="text"
              style={{ width: "100%", textAlign: "start", fontSize: sizes.medium, background: "transparent" }}
              onClick={selectOther}
            >
              {t("other")}
            </Button>
          </>
        )
      }
    : {};

  return (
    <Form.Item name={props.name} label={label} rules={[{ required }, ...rules]}>
      <SingleSelect
        useCommonStyle={false}
        listHeight={200}
        bordered
        placeholder={placeholder}
        onChange={updateFormOnFacilityChange}
        {...otherOptionProps}
      >
        {/* The Option VALUE is the facility's numeric id, so the mapped `facility.id` -> `facilityId`
            field carries the id the BE expects (not the name); the label still shows the name via the
            Option children. The hand-written form used `value={facility.id}` too; the JSON engine's
            generic item had regressed it to the name (MVP-31021, Jehad QA Finding B). */}
        {facilities?.map((facility) => (
          <Option key={facility.id} value={facility.id}>
            {facility.name}
          </Option>
        ))}
        {allowOther && !otherOptionId && (
          <Option key="other" value={OTHER}>
            {t("other")}
          </Option>
        )}
      </SingleSelect>
    </Form.Item>
  );
};
